import { Component, signal, inject, OnInit, OnDestroy } from '@angular/core';

import { Router } from '@angular/router';
import { ChatPrompt } from '../shared/chat-prompt/chat-prompt';
import { AuroraLight } from '../shared/aurora-light/aurora-light';
import { PROMPT_CATEGORIES } from './constants';
import { SphereAnimation } from './sphere-animation/sphere-animation';
import { ChatService } from '../services/chat-service/chat.service';
import { Greeting } from './greeting/greeting';
import { RecentBookmarks } from './recent-bookmarks/recent-bookmarks';
import { ContributeAssetButton } from '../shared/contribute-asset';

@Component({
  selector: 'home-page',
  standalone: true,
  imports: [
    ChatPrompt,
    AuroraLight,
    SphereAnimation,
    Greeting,
    RecentBookmarks,
    ContributeAssetButton,
  ],
  templateUrl: './home-page.html',
  styleUrl: './home-page.scss',
})
export class HomePage implements OnInit, OnDestroy {
  readonly router = inject(Router);
  readonly chatService = inject(ChatService);

  // ── Animated placeholder ───────────────────────────────────────
  readonly phSuggestions = ['Datasets', 'AI Agents', 'AI Tools', 'AI Products'];
  readonly phIndex = signal(0);
  readonly phPrev = signal(0);
  readonly phTransitioning = signal(false);
  private phTimer?: ReturnType<typeof setInterval>;
  private phTransTimer?: ReturnType<typeof setTimeout>;

  userInput = signal('');

  ngOnInit() {
    this.startPhRotation();
  }

  ngOnDestroy() {
    clearInterval(this.phTimer);
    clearTimeout(this.phTransTimer);
  }

  private startPhRotation() {
    this.phTimer = setInterval(() => {
      this.phPrev.set(this.phIndex());
      this.phIndex.set((this.phIndex() + 1) % this.phSuggestions.length);
      this.phTransitioning.set(true);
      clearTimeout(this.phTransTimer);
      this.phTransTimer = setTimeout(() => {
        this.phTransitioning.set(false);
      }, 400);
    }, 3000);
  }

  readonly promptCategories = PROMPT_CATEGORIES;
  readonly activePromptCategory = signal<string | null>(null);
  readonly dropdownPos = signal<{ top: number; left: number; width: number } | null>(null);

  togglePromptCategory(label: string, event: Event) {
    event.stopPropagation();
    const opening = this.activePromptCategory() !== label;
    this.activePromptCategory.update((cur) => (cur === label ? null : label));
    if (opening) {
      const container = (event.currentTarget as HTMLElement).closest(
        '.home__prompts',
      ) as HTMLElement;
      const r = container.getBoundingClientRect();
      this.dropdownPos.set({ top: r.bottom + 8, left: r.left, width: r.width });
    } else {
      this.dropdownPos.set(null);
    }
  }

  applyPrompt = (prompt: string) => {
    this.activePromptCategory.set(null);
    this.userInput.set(prompt);
  };

  initiateNewChat = async (userQuery: string) => {
    this.chatService.createNewChat().subscribe(({ chat_id }) => {
      this.router.navigate(['/chat', chat_id], {
        state: {
          userQuery,
        },
      });
    });
  };
}
